import { useLayoutEffect } from 'react';

/** Set on the container when even the smallest size can't fit a line, so the stylesheet can let it wrap. */
const TEXT_FIT_WRAP_ATTR = 'data-text-fit-wrap';

/**
 * Shrinks a heading's font size just enough that each of its lines fits on a
 * single line inside the heading's width, instead of breaking a long word
 * onto the next line (e.g. a club name like "Pickcoolbowl" in a narrow panel).
 *
 * The stylesheet's font size is the largest size used; nothing is ever grown.
 * If a line still wouldn't fit at `minFontPx`, the heading is set to that size
 * and gets TEXT_FIT_WRAP_ATTR so the stylesheet can let it wrap rather than
 * shrink into something unreadable.
 *
 * Re-fits when the heading's width changes (rotation, photos loading and
 * changing the layout) and once web fonts finish loading, since both change
 * how wide the text is.
 *
 * @param {{current: HTMLElement|null}} containerRef - the heading; lines inherit its font size
 * @param {string} lineSelector - CSS selector for the lines that must each stay on one line
 * @param {number} minFontPx - smallest font size, in px, to shrink to
 * @param {Array<unknown>} deps - re-fit when these change (the heading's text)
 * @returns {void}
 */
function useFitTextToWidth(containerRef, lineSelector, minFontPx, deps) {
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let lastWidth = null;
    const fit = () => {
      // Back to the stylesheet size and unwrapped lines first, so every pass
      // measures from scratch — otherwise the text could only ever shrink.
      container.style.removeProperty('font-size');
      container.removeAttribute(TEXT_FIT_WRAP_ATTR);

      const available = container.clientWidth;
      const lines = container.querySelectorAll(lineSelector);
      if (!available || lines.length === 0) return;

      let widest = 0;
      lines.forEach((line) => {
        widest = Math.max(widest, line.getBoundingClientRect().width);
      });
      if (widest <= available) return;

      const baseSize = parseFloat(window.getComputedStyle(container).fontSize);
      if (!baseSize) return;

      // Text width scales linearly with font size (letter-spacing is in em),
      // so one ratio is enough. Rounded down to a tenth of a pixel so rounding
      // can't push the line a pixel past the edge.
      const fitted = Math.floor(baseSize * (available / widest) * 10) / 10;
      const floor = Math.min(minFontPx, baseSize);
      if (fitted >= floor) {
        container.style.fontSize = `${fitted}px`;
        return;
      }
      container.style.fontSize = `${floor}px`;
      container.setAttribute(TEXT_FIT_WRAP_ATTR, 'true');
    };

    fit();

    // Width changes only: a smaller font changes the heading's height, never
    // its width, so this can't feed back into itself. The re-fit still runs on
    // the next frame, not inside the callback: resizing the heading while the
    // observer is delivering makes the browser raise "ResizeObserver loop
    // completed with undelivered notifications" (shown as a runtime error in dev).
    let observer = null;
    let frameId = 0;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect?.width;
        if (width === lastWidth) return;
        lastWidth = width;
        cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(fit);
      });
      observer.observe(container);
    }

    let cancelled = false;
    document.fonts?.ready?.then(() => { if (!cancelled) fit(); });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default useFitTextToWidth;
